// src/pages/AdminOrders.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './AdminOrders.css';
import OrderModal from '../components/OrderModal';
import { useToast } from '../components/ToastProvider';
import { adminFetch } from '../api/admin';
import { Check, Truck, Eye, Trash2, LogOut, BarChart, DollarSign } from 'lucide-react';
import ShippingReportModal from '../components/ShippingReportModal';

const PAGE_SIZE = 20;
const STATUS_OPTIONS = ['processing', 'shipped', 'delivered', 'cancelled'];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusDrafts, setStatusDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [page, setPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedDate, setSelectedDate] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);

  const { showToast } = useToast();
  const navigate = useNavigate();

  const stats = useMemo(() => {
    if (!orders || orders.length === 0) {
      return { todayCount: 0, processingCount: 0, deliveredCount: 0 };
    }

    const todayStr = new Date().toISOString().slice(0, 10);

    let todayCount = 0;
    let processingCount = 0;
    let deliveredCount = 0;

    for (const order of orders) {
      if (!order) continue;

      if ((order.status ?? '').toLowerCase() === 'processing') {
        processingCount += 1;
      }

      if ((order.status ?? '').toLowerCase() === 'delivered') {
        deliveredCount += 1;
      }

      const rawCreated = order.created_at;
      if (rawCreated) {
        try {
          const d = new Date(rawCreated);
          if (!Number.isNaN(d.getTime())) {
            const orderDateStr = d.toISOString().slice(0, 10);
            if (orderDateStr === todayStr) {
              todayCount += 1;
            }
          }
        } catch {
          // ignore invalid dates
        }
      }
    }

    return { todayCount, processingCount, deliveredCount };
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    const query = searchQuery.trim().toLowerCase();
    const statusValue = statusFilter.toLowerCase();
    const dateValue = selectedDate;

    return orders.filter((order) => {
      if (!order) return false;

      // 🔍 Search filter
      if (query) {
        const idMatch = String(order.id ?? '')
          .toLowerCase()
          .includes(query);
        const nameMatch = (order.customer_name ?? '')
          .toLowerCase()
          .includes(query);
        const statusMatch = (order.status ?? '')
          .toLowerCase()
          .includes(query);

        if (!idMatch && !nameMatch && !statusMatch) {
          return false;
        }
      }

      // ✅ Status filter
      if (statusValue !== 'all') {
        if ((order.status ?? '').toLowerCase() !== statusValue) {
          return false;
        }
      }

      // 📅 Date filter
      if (dateValue) {
        const rawCreated = order.created_at;
        if (!rawCreated) {
          return false;
        }

        let orderDateStr = '';
        try {
          const d = new Date(rawCreated);
          if (!Number.isNaN(d.getTime())) {
            orderDateStr = d.toISOString().slice(0, 10);
          }
        } catch {
          orderDateStr = '';
        }

        if (!orderDateStr || orderDateStr !== dateValue) {
          return false;
        }
      }

      return true;
    });
  }, [orders, searchQuery, statusFilter, selectedDate]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));

  const pageOrders = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredOrders.slice(start, start + PAGE_SIZE);
  }, [filteredOrders, page]);

  // Reset to first page when filters change so pagination remains valid
  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, selectedDate]);

  const handleExportCsv = useCallback(() => {
    if (!filteredOrders || filteredOrders.length === 0) return;

    const escape = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      // Escape double quotes by doubling them
      const escaped = str.replace(/"/g, '""');
      return `"${escaped}"`;
    };

    const header = [
      'Order ID',
      'Customer Name',
      'Items Count',
      'Total Amount',
      'Status',
      'Created Date',
    ];

    const rows = filteredOrders.map((order) => [
      escape(order.id),
      escape(order.customer_name),
      escape(order.items_count),
      escape(order.total),
      escape(order.status),
      escape(order.created_at),
    ]);

    const csv = [
      header.join(','),
      ...rows.map((cols) => cols.join(',')),
    ].join('\r\n');

    const todayStr = new Date().toISOString().slice(0, 10);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `orders-${todayStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredOrders]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminFetch(`get-orders.php`);

      if (!res.ok) {
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error(`HTTP ${res.status}: ${text.substring(0, 100) || 'Server error'}`);
        }
        throw new Error(data.error || data.message || `HTTP ${res.status}`);
      }

      const data = await res.json();

      if (!data.ok) throw new Error(data.error || data.message || 'Failed to load orders');
      setOrders(data.orders || []);
      setPage(1);
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchOrderDetails = async (orderId) => {
    try {
      const res = await adminFetch(`get-order.php?order_id=${orderId}`);

      if (!res.ok) {
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error(`HTTP ${res.status}: ${text.substring(0, 100)}`);
        }
        throw new Error(data.error || data.message || `HTTP ${res.status}`);
      }

      const data = await res.json();

      if (!data.ok) {
        throw new Error(data.error || data.message || 'Failed to load order');
      }

      if (!data.order) {
        throw new Error('Invalid response: missing order data');
      }

      setSelectedOrder(data.order);
    } catch (err) {
      console.error('Failed to load order details:', err);
      showToast(err.message || 'Failed to load order details', 'error');
    }
  };

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
    setSelectedOrderId(null);
  }, []);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      setAuthLoading(true);
      try {
        const res = await adminFetch('admin-me.php');
        const data = await res.json();

        if (!data.success || !data.authenticated) {
          navigate('/admin/login');
        } else {
          setAuthLoading(false);
        }
      } catch (err) {
        navigate('/admin/login');
      }
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      const res = await adminFetch('admin-logout.php', {
        method: 'POST',
      });
      const data = await res.json();

      if (data.success) {
        showToast('Logged out successfully', 'success');
        navigate('/admin/login');
      }
    } catch (err) {
      // Even if logout fails, redirect to login
      navigate('/admin/login');
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchOrders();
    }
  }, [fetchOrders, authLoading]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && showDeleteModal) {
        handleDeleteCancel();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showDeleteModal, handleDeleteCancel]);

  const handleSaveStatus = async (order) => {
    const nextStatus = statusDrafts[order.id];
    if (!nextStatus) return;

    setSavingId(order.id);
    try {
      const res = await adminFetch(`update-order.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: order.id, status: nextStatus }),
      });

      const data = await res.json();
      if (!data.ok) throw new Error(data.error);

      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id
            ? { ...o, status: nextStatus, delivered_at: nextStatus === 'delivered' ? new Date().toISOString() : o.delivered_at }
            : o
        )
      );

      // Clear status draft
      setStatusDrafts((prev) => {
        const next = { ...prev };
        delete next[order.id];
        return next;
      });

      showToast('Status updated', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleMarkDelivered = async (order) => {
    if (order.status === 'delivered') return;

    setSavingId(order.id);
    try {
      const res = await adminFetch(`update-order.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: order.id, status: 'delivered' }),
      });

      const data = await res.json();
      if (!data.ok) throw new Error(data.error);

      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id
            ? { ...o, status: 'delivered', delivered_at: new Date().toISOString() }
            : o
        )
      );

      showToast('Order marked as delivered', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteClick = (order) => {
    setSelectedOrderId(order.id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedOrderId) return;

    const order = orders.find((o) => o.id === selectedOrderId);
    if (!order) return;

    setSavingId(order.id);
    setShowDeleteModal(false);

    try {
      const res = await adminFetch(`delete-order.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: order.id }),
      });

      const data = await res.json();
      if (!data.ok) throw new Error(data.error);

      // Remove order from UI
      setOrders((prev) => prev.filter((o) => o.id !== order.id));

      showToast('Order deleted', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingId(null);
      setSelectedOrderId(null);
    }
  };

  if (authLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Checking authentication...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 className="admin-title">Orders</h1>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Link
            to="/admin/payments"
            className="btn-pill primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}
          >
            <DollarSign size={16} />
            Payment Analytics
          </Link>
          <button
            onClick={handleLogout}
            className="btn-pill secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </div>

      <div className="admin-stats">
        <div className="admin-stat-card">
          <div className="admin-stat-label">Today's Orders</div>
          <div className="admin-stat-value">{stats.todayCount}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-label">Processing Orders</div>
          <div className="admin-stat-value">{stats.processingCount}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-label">Delivered Orders</div>
          <div className="admin-stat-value">{stats.deliveredCount}</div>
        </div>
      </div>

      <div className="admin-filters">
        <input
          type="text"
          className="admin-filter-input"
          placeholder="Search by order ID, customer, or status"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <select
          className="admin-filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All">All</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <input
          type="date"
          className="admin-filter-date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          placeholder="Filter by date"
          aria-label="Filter by date"
        />

        <button
          type="button"
          className="admin-clear-filters-btn"
          onClick={() => {
            setSearchQuery('');
            setStatusFilter('All');
            setSelectedDate('');
          }}
          disabled={
            searchQuery === '' &&
            statusFilter === 'All' &&
            selectedDate === ''
          }
        >
          Clear filters
        </button>

        <button
          type="button"
          className="admin-export-btn"
          onClick={handleExportCsv}
          disabled={!filteredOrders || filteredOrders.length === 0}
        >
          Export CSV
        </button>

        <button
          type="button"
          className="admin-export-btn"
          style={{ background: '#f8f9fa', color: '#d14c87', border: '1px solid #d14c87' }}
          onClick={() => setShowReportModal(true)}
        >
          <BarChart size={16} style={{ marginRight: '6px' }} />
          Reports
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {loading && <div className="admin-loading">Loading orders…</div>}

      {!loading && pageOrders.length === 0 && (
        <div className="admin-empty">No orders found</div>
      )}

      {pageOrders.map((order) => (
        <div key={order.id} className="order-card">
          {/* 🔹 CLEAN ROW VIEW */}
          <div className="order-top">
            <div>
              <div className="order-label">Order</div>
              <div className="order-value">#{order.id}</div>
            </div>

            <div>
              <div className="order-label">Customer</div>
              <div className="order-value">{order.customer_name}</div>
            </div>

            <div>
              <div className="order-label">Items</div>
              <div className="order-value">
                {order.items_count > 0
                  ? `${order.items_count} ${order.items_count === 1 ? 'item' : 'items'}`
                  : '—'}
              </div>
            </div>

            <div>
              <div className="order-label">Total</div>
              <div className="order-value">₹{order.total}</div>
            </div>

            <div>
              <div className="order-label">Status</div>
              <span className={`order-chip order-chip-${(order.status || '').toLowerCase()}`}>
                {order.status}
              </span>
            </div>
          </div>

          {/* 🔹 ACTIONS */}
          <div className="order-footer">
            <div className="order-actions-left">
              <select
                className="order-select"
                value={statusDrafts[order.id] || ''}
                onChange={(e) =>
                  setStatusDrafts({
                    ...statusDrafts,
                    [order.id]: e.target.value,
                  })
                }
                disabled={savingId === order.id || order.status === 'delivered'}
              >
                <option value="">Select status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              <button
                onClick={() => handleSaveStatus(order)}
                disabled={savingId === order.id || !statusDrafts[order.id] || order.status === 'delivered'}
              >
                <Check size={16} />
                {savingId === order.id ? 'Saving…' : 'Save'}
              </button>
            </div>

            {order.status !== 'delivered' && (
              <div className="order-actions-primary">
                <button
                  className="btn-pill delivered"
                  onClick={() => handleMarkDelivered(order)}
                  disabled={savingId === order.id}
                >
                  <Truck size={16} />
                  Mark as Delivered
                </button>
              </div>
            )}

            <div className="order-actions-secondary">
              <button
                className="btn-pill secondary"
                onClick={() => fetchOrderDetails(order.id)}
                disabled={savingId === order.id}
              >
                <Eye size={16} />
                View details
              </button>
            </div>

            <div className="order-actions-danger">
              <button
                className="btn-pill danger"
                onClick={() => handleDeleteClick(order)}
                disabled={savingId === order.id}
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}

      {totalPages > 1 && (
        <div className="admin-pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Prev
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {selectedOrder && (
        <OrderModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}

      {showReportModal && (
        <ShippingReportModal onClose={() => setShowReportModal(false)} />
      )}

      {showDeleteModal && (
        <div className="delete-modal-backdrop" onClick={handleDeleteCancel}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-card">
              <h3 className="delete-modal-title">Delete Order?</h3>
              <p className="delete-modal-message">
                This action cannot be undone.
              </p>
              <div className="delete-modal-actions">
                <button
                  className="btn-pill secondary"
                  onClick={handleDeleteCancel}
                >
                  Cancel
                </button>
                <button
                  className="btn-pill danger"
                  onClick={handleDeleteConfirm}
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
